/**
 * @fileoverview Match3 Game Types
 * @description Type definitions for the Match3 puzzle game
 */

/**
 * Gem types available in the game
 */
export type GemType = "red" | "blue" | "green" | "yellow" | "purple" | "orange";

/**
 * Special tile types (power-ups created by matching 4+ gems)
 */
export type SpecialType = "none" | "bomb" | "lightning" | "rainbow";

/**
 * Individual tile in the grid
 */
export interface Match3Tile {
  /** Unique identifier */
  id: string;
  /** Row position (0-7) */
  row: number;
  /** Column position (0-7) */
  col: number;
  /** Gem color type */
  gemType: GemType;
  /** Special power-up type */
  specialType: SpecialType;
  /** Whether tile is selected */
  isSelected: boolean;
  /** Whether tile is being removed (for animation) */
  isRemoving: boolean;
  /** Whether tile is falling (for animation) */
  isFalling: boolean;
  /** Whether tile is new (just spawned) */
  isNew: boolean;
}

/**
 * Position in the grid
 */
export interface GridPosition {
  row: number;
  col: number;
}

/**
 * Match result from checking the grid
 */
export interface MatchResult {
  /** Tiles that were matched */
  matchedTiles: GridPosition[];
  /** Type of match (3, 4, 5, L-shape, T-shape) */
  matchType: "three" | "four" | "five" | "l_shape" | "t_shape";
  /** Points earned from this match */
  points: number;
  /** Special tile to create (if any) */
  createsSpecial: SpecialType;
  /** Position to create special tile */
  specialPosition?: GridPosition;
}

/**
 * Game state
 */
export interface Match3GameState {
  /** 8x8 grid of tiles */
  grid: Match3Tile[][];
  /** Current score */
  score: number;
  /** Time remaining in seconds */
  timeRemaining: number;
  /** Initial time (for reference) */
  initialTime: number;
  /** Current combo level (resets after no cascades) */
  comboLevel: number;
  /** Total moves made */
  movesMade: number;
  /** Whether game is active */
  isPlaying: boolean;
  /** Whether game is paused */
  isPaused: boolean;
  /** Whether game is over */
  isGameOver: boolean;
  /** Whether currently processing matches/cascades */
  isProcessing: boolean;
  /** Selected tile for swap */
  selectedTile: GridPosition | null;
  /** Last match for animation purposes */
  lastMatch: MatchResult | null;
  /** Start time */
  startedAt: string | null;
  /** End time */
  endedAt: string | null;
  /** Time bonuses earned */
  timeBonusesEarned: number;
  /** Power-ups used */
  powerUpsUsed: number;
}

/**
 * Game result
 */
export interface Match3Result {
  /** Final score */
  score: number;
  /** Time played in seconds */
  timePlayedSeconds: number;
  /** Total moves made */
  movesMade: number;
  /** Combo level achieved */
  maxCombo: number;
  /** Power-ups used */
  powerUpsUsed: number;
  /** Time bonuses earned */
  timeBonusesEarned: number;
  /** Points earned (weekend mode) */
  pointsEarned: number;
  /** Whether this is a new high score */
  isNewHighScore: boolean;
  /** Whether this is a new family record */
  isNewFamilyRecord: boolean;
  /** Milestones achieved */
  milestonesAchieved: string[];
}

/**
 * Scoring configuration
 */
export interface Match3ScoringConfig {
  /** Points for 3 in a row */
  points_three: number;
  /** Points for 4 in a row */
  points_four: number;
  /** Points for 5 in a row */
  points_five: number;
  /** Points for L/T shape */
  points_special_shape: number;
  /** Combo multiplier per cascade level */
  combo_multiplier: number;
}

/**
 * Time bonus configuration
 */
export interface Match3TimeBonusConfig {
  /** Score threshold to earn time bonus */
  score_threshold: number;
  /** Seconds to add when threshold reached */
  bonus_seconds: number;
  /** Label for display */
  label: string;
}

/**
 * Game configuration (super admin settable)
 */
export interface Match3GameConfig {
  /** Initial time in seconds */
  initial_time_seconds: number;
  /** Grid size (always 8 for Match3) */
  grid_size: number;
  /** Scoring configuration */
  scoring: Match3ScoringConfig;
  /** Time bonuses (earn extra time at score thresholds) */
  time_bonuses: Match3TimeBonusConfig[];
  /** Lottie animation for win */
  win_lottie?: string;
  /** Lottie animation for high score */
  high_score_lottie?: string;
}

/**
 * Weekend mode configuration (familie admin settable)
 */
export interface Match3WeekendConfig {
  /** Points for participation */
  points_participation: number;
  /** Points for new family record */
  points_new_highscore: number;
  /** Milestone points: score >= 500 */
  points_score_500: number;
  /** Milestone points: score >= 1000 */
  points_score_1000: number;
  /** Milestone points: score >= 2000 */
  points_score_2000: number;
  /** Milestone points: score >= 5000 */
  points_score_5000: number;
  /** Max plays per day */
  max_plays_per_day: number;
  /** Allow unlimited plays */
  allow_unlimited_plays: boolean;
  /** Custom time bonuses for weekend mode (overrides super admin defaults if set) */
  time_bonuses?: Match3TimeBonusConfig[];
}

/**
 * User stats for Match3
 */
export interface Match3UserStats {
  /** Total games played */
  games_played: number;
  /** Best score achieved */
  best_score: number;
  /** Total score across all games */
  total_score: number;
  /** Best combo achieved */
  best_combo: number;
  /** Total moves made */
  total_moves: number;
  /** Total time played in seconds */
  total_time_seconds: number;
  /** Total power-ups used */
  total_power_ups_used: number;
  /** Best time bonuses earned in a single game */
  best_time_bonuses: number;
}

/**
 * Default game configuration
 */
export const DEFAULT_MATCH3_GAME_CONFIG: Match3GameConfig = {
  initial_time_seconds: 120,
  grid_size: 8,
  scoring: {
    points_three: 30,
    points_four: 60,
    points_five: 150,
    points_special_shape: 100,
    combo_multiplier: 1.5,
  },
  time_bonuses: [
    { score_threshold: 500, bonus_seconds: 15, label: "+15s bij 500 punten" },
    { score_threshold: 1500, bonus_seconds: 20, label: "+20s bij 1500 punten" },
    { score_threshold: 3000, bonus_seconds: 30, label: "+30s bij 3000 punten" },
    { score_threshold: 5000, bonus_seconds: 45, label: "+45s bij 5000 punten" },
  ],
};

/**
 * Default weekend configuration
 */
export const DEFAULT_MATCH3_WEEKEND_CONFIG: Match3WeekendConfig = {
  points_participation: 5,
  points_new_highscore: 25,
  points_score_500: 10,
  points_score_1000: 20,
  points_score_2000: 40,
  points_score_5000: 100,
  max_plays_per_day: 10,
  allow_unlimited_plays: false,
};

/**
 * Default empty stats
 */
export const EMPTY_MATCH3_STATS: Match3UserStats = {
  games_played: 0,
  best_score: 0,
  total_score: 0,
  best_combo: 0,
  total_moves: 0,
  total_time_seconds: 0,
  total_power_ups_used: 0,
  best_time_bonuses: 0,
};

/**
 * Gem colors for rendering
 */
export const GEM_COLORS: Record<GemType, { primary: string; secondary: string; glow: string }> = {
  red: { primary: "#EF4444", secondary: "#DC2626", glow: "#FCA5A5" },
  blue: { primary: "#3B82F6", secondary: "#2563EB", glow: "#93C5FD" },
  green: { primary: "#22C55E", secondary: "#16A34A", glow: "#86EFAC" },
  yellow: { primary: "#EAB308", secondary: "#CA8A04", glow: "#FDE047" },
  purple: { primary: "#A855F7", secondary: "#9333EA", glow: "#D8B4FE" },
  orange: { primary: "#F97316", secondary: "#EA580C", glow: "#FDBA74" },
};

/**
 * All gem types for random selection
 */
export const ALL_GEM_TYPES: GemType[] = ["red", "blue", "green", "yellow", "purple", "orange"];

/**
 * Special tile icons/labels
 */
export const SPECIAL_TILE_INFO: Record<SpecialType, { icon: string; label: string; description: string }> = {
  none: { icon: "", label: "", description: "" },
  bomb: { icon: "💣", label: "Bom", description: "Verwijdert 3x3 gebied" },
  lightning: { icon: "⚡", label: "Bliksem", description: "Verwijdert hele rij + kolom" },
  rainbow: { icon: "🌈", label: "Regenboog", description: "Verwijdert alle van één kleur" },
};

